import {
  ChangeEvent,
  cloneElement,
  FC,
  FocusEvent,
  MouseEvent,
  ReactElement,
  Ref,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import InputMask from 'react-input-mask';
import clsx from 'clsx';
import { isUndefined } from 'lodash-es';
import { Icon } from '@namespace/icons2';
import { useGetThemeConfig } from '@namespace/themes';
import { useCombinedRefs } from '../../hooks/useCombinedRefs';
import { propsMerge } from '../../utils/propsMerge';
import { Box } from '../Box';
import { Label } from '../Label';

import styles from './index.module.css';

const maybeEmptyString = (a: string | null | undefined): string => a || '';

const InputMaskForward = InputMask;

export interface InputProps {
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'hidden';
  className?: string;
  isFullFieldBorder?: boolean;
  labelClassName?: string;
  placeholder?: string;
  wrapperClassName?: string;
  clearable?: boolean;
  onClean?: (value: string) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  align?: 'left' | 'center' | 'right';
  size?: 'xs' | 's' | 'm';
  iconSize?: string | number;
  value?: string;
  icon?: ReactElement;
  iconLeft?: ReactElement;
  intent?: 'error' | 'success' | 'default' | 'ghost';
  mask?: string | ((newIsoCode: unknown) => void) | null;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  dataRole?: string;
  validator?: (value: string) => boolean;
  bottomElement?: ReactElement;
  postInputLabel?: string;
  id?: string;
  readOnly?: boolean;
  maxLength?: number;
  min?: number;
  disabled?: boolean;
  autoComplete?: string;
  maskChar?: string;
  onClick?: () => void;
  isDisabledBottomElement?: boolean;
}

type MaskedInputRef = HTMLInputElement & {
  getInputDOMNode?: () => HTMLInputElement;
};

export const Input: FC<InputProps> = (props) => {
  const inputConfig = useGetThemeConfig('Input');
  const {
    inputRef = null,
    type = 'text',
    className = '',
    labelClassName = '',
    isFullFieldBorder = false,
    placeholder = '',
    wrapperClassName = '',
    clearable = false,
    onClean,
    onChange,
    align = 'left',
    size = 's',
    iconSize,
    value = '',
    icon = null,
    iconLeft = null,
    intent = 'default',
    mask,
    onFocus,
    onBlur,
    dataRole,
    validator,
    bottomElement = null,
    postInputLabel,
    isDisabledBottomElement = false,
    id,
    ...rest
  } = propsMerge(props, inputConfig);

  const isFocusedRef = useRef(false);
  const innerRef = useRef<MaskedInputRef | null>(null);
  const combinedRef = useCombinedRefs(inputRef, innerRef);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  useEffect(() => {
    if (isFocusedRef.current) {
      if (mask && innerRef.current?.getInputDOMNode) {
        innerRef.current.getInputDOMNode().focus();
      } else {
        innerRef.current?.focus();
      }
    }
  }, [mask]);

  const togglePasswordVisibility = useCallback(
    () => setIsPasswordVisible((prevState) => !prevState),
    [setIsPasswordVisible]
  );

  const clean = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
      onClean?.('');

      innerRef.current?.focus();
    },
    [onClean, onChange]
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = true;
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      isFocusedRef.current = false;
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isNotValid = !isUndefined(validator) && !validator(e.target.value);
      return isNotValid ? undefined : onChange?.(e);
    },
    [validator, onChange]
  );

  const wrapperClasses = clsx([
    styles.wrapper,
    wrapperClassName,
    styles[align],
    styles[size],
    styles[intent],
    clearable && styles.isClearable,
    icon && styles.withIcon,
    iconLeft && styles.iconLeft,
    type === 'password' && styles.password
  ]);

  const InputComponent = mask ? InputMaskForward : 'input';

  return (
    <Box data-role={dataRole} className={wrapperClasses} direction="column">
      {iconLeft && (
        <Box
          component="span"
          className={styles.iconLeftContainer}
          direction="column"
          align="center"
          justify="center"
        >
          {cloneElement(iconLeft, { size: iconSize || size })}
        </Box>
      )}
      <InputComponent
        ref={combinedRef}
        type={isPasswordVisible ? 'text' : type}
        className={clsx([
          styles.input,
          className,
          isFullFieldBorder ? styles.fullBorder : ''
        ])}
        placeholder={placeholder}
        value={maybeEmptyString(value)}
        onChange={handleChange}
        mask={mask}
        onFocus={handleFocus}
        onBlur={handleBlur}
        id={id}
        {...rest}
      />
      {postInputLabel && (
        <Label htmlFor={id} className={clsx([styles.label, labelClassName])}>
          {postInputLabel}
        </Label>
      )}
      {clearable && value && (
        <Box
          data-role="input-clear-button"
          onClick={clean}
          className={styles.clear}
          direction="column"
        >
          <Icon size={size} name="icons/general/action/close" />
        </Box>
      )}
      {type === 'password' && (
        <Box
          data-role={`password-eye-${isPasswordVisible ? 'hide' : 'show'}`}
          component="span"
          onClick={togglePasswordVisibility}
          className={styles.showPassIcon}
          align="center"
          justify="center"
        >
          <Icon
            className={styles.passIcon}
            size={size}
            name={
              isPasswordVisible
                ? 'icons/general/state/hide'
                : 'icons/general/state/show'
            }
          />
        </Box>
      )}
      {icon && (
        <Box
          component="span"
          className={styles.iconContainer}
          direction="column"
        >
          {cloneElement(icon, { size: iconSize || size })}
        </Box>
      )}
      {bottomElement &&
        intent !== 'ghost' &&
        cloneElement(bottomElement, {
          className: styles.borderBottomCustom,
          disabled: isDisabledBottomElement
        })}
    </Box>
  );
};
